/**
 * @module app.shared
 */

import { ReadonlyIndexed } from 'app/shared/lib/types';
import { mergeObjects, encodeQueryString } from 'app/shared/lib/utilities';
import { QueryModelState, IQueryLoadOptions, IQuerySort, IQuerySortingOptions, IQueryPaginationOptions, IApiStandardLoadOptionsApplyOptions, QueryFilterGroupOperator, QuerySessionValue, IQueryInclude, QueryEqualityOperator, QueryStringComparisonOperator, QueryComparisonOperator, QuerySpecialDate, QuerySpecialDateTime, QueryLimit, IQuerySortGroup } from './api-types';
import { ApiQueryFilterGroup, ApiQueryDirectFilter, ApiQueryNotFilter } from './api-filters';
import { ApiQueryExistsFilter, IQueryExistsFilterOptions } from './api-filters-exists';
import { ApiQueryIsNullFilter } from './api-filters-isnull';
import { ApiQueryIdentifierFilter, IQueryIdentifierFilterOptions } from './api-filters-identifier';
import { ApiQueryStringFilter, IQueryStringFilterOptions } from './api-filters-string';
import { ApiQueryDateFilter, ApiQueryDateTimeOnDateFilter, ApiQueryDateTimeBetweenDatesFilter, IQueryDateTimeFilterOptions, IQueryDateTimeBetweenFilterOptions, ApiQueryDateBetweenFilter, ApiQueryDateTimeFilter, IQueryDateTimeOnFilterOptions, ApiQuerySpecialDateFilter, ApiQuerySpecialDateTimeFilter, ApiQueryDateTimeBetweenFilter } from './api-filters-datetime';
import { ApiQueryIdentifierArrayFilter, ApiQueryStringArrayFilter, IQueryArrayFilterOptions } from './api-filters-array';
import { ApiQueryBooleanFilter, IQueryBooleanFilterOptions } from './api-filters-boolean';
import { ApiQuerySessionFilter } from './api-filters-session';
import { DateValue, DateTimeValue } from 'app/shared/lib/date-time';

interface IQueryInternals {
    tenant: string | null;
    url: string | null;
    state: QueryModelState | null;
    strategy: string | null;
    includes: Map<string, IQueryInclude>;
    fields: string[];
    feature: string | null;
    groups: Map<string, IQuerySortGroup>;
    orders: Map<string, IQuerySort>;
    search: string | null;
    filterGroups: ApiQueryFilterGroup[];
    attachedTo: {
        code: string | null;
        id: string | null;
    };
    page: {
        top: number | null;
        skip: number | null;
        limit: QueryLimit | null;
        count: 'only' | 'none' | null;
    },
    detailPage: {
        top: number | null;
        skip: number | null;
        limit: QueryLimit | null;
    }
    parameters: Map<string, string>;
}

// NOTE: this is a union of all of the various filter options interfaces
interface IFilterOptions {
    readonly ignoreNullValue: boolean;
    readonly negate: boolean;
    readonly operator: string;
    readonly omitWhenEmpty: false;
}

interface IApiQueryDefaults {
    queryLoadOptions: IQueryLoadOptions,
    filterOptions: IFilterOptions
}

/**
 * API Query Builder
 */
export class ApiQuery {

    private static readonly allRecordLimit = 1000000; // one million records, mwa ha ha
    private static readonly allDetailLimit = 10000; // 10 thousand records

    private static readonly defaults: IApiQueryDefaults = {
        queryLoadOptions: {
            all: false,
            top: null,
            skip: null,
            page: null,
            includeInactive: false,
            sorting: {
                entries: [],
                groupEntries: []
            },
            pagination: { size: 15, count: null, index: 0 }
        },
        filterOptions: {
            ignoreNullValue: false,
            negate: false,
            operator: 'eq',
            omitWhenEmpty: false
        }
    };

    private readonly internals: IQueryInternals = {
        tenant: null,
        url: null,
        state: null,
        strategy: null,
        includes: new Map<string, IQueryInclude>(),
        fields: [],
        feature: null,
        groups: new Map<string, IQuerySortGroup>(),
        orders: new Map<string, IQuerySort>(),
        search: null,
        filterGroups: [],
        attachedTo: { code: null, id: null },
        page: { top: null, skip: null, limit: null, count: null },
        detailPage: { top: null, skip: null, limit: null },
        parameters: new Map<string, string>()
    };

    /**
     * Construct a new API Query object
     *
     * **Note**: `api.query()` is a shortcut to constructing this object
     * @param url the API URL to query against
     */
    constructor(url: string) {
        this.internals.url = url;

        const rootFilterGroup = new ApiQueryFilterGroup('and');
        rootFilterGroup.parenthesize = false;
        this.internals.filterGroups.push(rootFilterGroup);
    }

    /** Returns `true` if this query has a tenant code value specified */
    public hasTenant(): boolean {
        return !!this.internals.tenant;
    }

    /**
     * Specify the tenant code to use for this query
     * @param tenant the tenant code to query, or `null` to use the currently logged in tenant
     */
    public withTenant(tenant: string | null): this {
        this.internals.tenant = tenant;
        return this;
    }

    /**
     * Override the URL used to make this query
     * @param url the new URL to query against
     */
    public overrideUrl(url: string): this {
        this.internals.url = url;
        return this;
    }

    /**
     * Include the list of specified model references, all with the given state
     * @param stateOrOptions `'active'`, `'inactive'`,`'inactiveOnly'`, or an `IQueryLoadOptions` object
     * @param references the model references to include
     */
    public includeWithState(stateOrOptions?: QueryModelState | Partial<IQueryLoadOptions>, ...references: string[]): this {
        if(!stateOrOptions) {
            return this;
        }

        let state: QueryModelState | null;
        if(typeof stateOrOptions === 'string') {
            state = stateOrOptions;
        }
        else {
            const appliedOptions = mergeObjects({ ...ApiQuery.defaults.queryLoadOptions }, stateOrOptions);
            const includeInactive = appliedOptions.includeInactive;
            state = includeInactive ? 'inactive' : null;
        }
        for(const reference of references) {
            this.internals.includes.set(reference.toUpperCase(), { reference, state });
        }
        return this;
    }

    /**
     * Include the list of specified model references
     * @param references the model references to include
     */
    public include(...references: string[]): this {
        for(const reference of references) {
            this.internals.includes.set(reference.toLowerCase(), { reference });
        }
        return this;
    }

    /**
    * Include the list of specified model reference fields
    * @param references the additional model reference fields to query for
    */
    public field(...references: string[]): this {
        for(const reference of references) {
            this.internals.fields.push(reference.toLowerCase());
        }
        return this;
    }

    /**
     * Include inactive results
     * @param include if `false` is specified, inactive records will NOT be included  *(optional)*
     */
    public includeInactive(include: boolean = true): this {
        if(include) {
            this.internals.state = 'inactive';
        }
        return this;
    }

    /**
     * Exclude active results
     * @param exclude if `false` is specified, active records will NOT be excluded  *(optional)*
     */
    public excludeActive(exclude: boolean = true): this {
        if(exclude) {
            this.internals.state = 'inactiveOnly';
        }
        return this;
    }

    /**
     * Exclude inactive results
     * @param exclude if `false` is specified, inactive records will NOT be excluded *(optional)*
     */
    public excludeInactive(exclude: boolean = true): this {
        if(exclude) {
            this.internals.state = 'active';
        }
        return this;
    }

    /**
     * Activate the specified feature
     * @param feature the feature to activate
     */
    public feature(feature: string): this {
        if(feature.length > 0) {
            this.internals.feature = feature;
        }
        return this;
    }

    /**
     * Include records attached to the specified model (code and id)
     * @param modelCode the model code of the attached target
     * @param id the identifier of the attached target
     */
    public attachedTo(modelCode: string | null, id: string | null): this {
        if(modelCode && id) {
            this.internals.attachedTo = { code: modelCode, id: id };
        }
        return this;
    }

    /**
     * Perform a general search of the models with the specified search string
     * @param value search text
     */
    public search(value: string | null): this {
        if(value) {
            this.internals.search = value;
        }
        return this;
    }

    /**
     * Assign a strategy to this query, useful for query generation tuning/hints
     * @param value strategy name
     * @returns this
     */
    public strategy(value: string | null): this {
        if(value) {
            this.internals.strategy = value;
        }
        return this;
    }

    /**
     * Assign a query limit. A value of `all` indicates that all records are to be returned.
     *
     * Otherwise, the `top` parameter is used to limit rows.
     * @param value `all` or `none`
     * @returns this
     */
    public limit(value: QueryLimit | null): this {
        if(value) {
            this.internals.page.limit = value;
        }
        return this;
    }

    private getCurrentFilterGroup(): ApiQueryFilterGroup {
        return this.internals.filterGroups[this.internals.filterGroups.length - 1];
    }

    private addFilterGroup(operator: QueryFilterGroupOperator, addFilters: (query: ApiQuery) => void): this {
        const group = new ApiQueryFilterGroup(operator);
        this.getCurrentFilterGroup().filters.push(group);
        this.internals.filterGroups.push(group);
        try {
            addFilters(this);
        }
        finally {
            this.internals.filterGroups.pop();
        }
        return this;
    }

    /**
     * Combines a group of filters into a logical **AND** group
     *
     * @example
     * ```ts
     * query.filterAnd(and => and
     *     .filterString('code', 'CODE1')
     *     .filterBoolean('isCurrent', true)
     * );
     * ```
     * @param addFilters callback that will be called to combine all following filter commands under a logical **AND** group
     */
    public filterAnd(addFilters: (query: ApiQuery) => void): this {
        this.addFilterGroup('and', addFilters);
        return this;
    }

    /**
     * Combines a group of filters into a logical **OR** group
     *
     * @example
     * ```ts
     *     query.filterOr(or => or
     *         .filterString('code', 'CODE1')
     *         .filterString('code', 'CODE2')
     *     );
     * ```
     * @param addFilters callback that will be called to combine all following filter commands under a logical **AND** group
     */
    public filterOr(addFilters: (query: ApiQuery) => void): this {
        this.addFilterGroup('or', addFilters);
        return this;
    }

    /**
     * Combines a group of filters into a logical **NOT** group
     *
     * @example
     * ```ts
     * query.filterNot(not => not
     *     .filterString('code', 'CODE1')
     *     .filterBoolean('isCurrent', true)
     * );
     * ```
     * @param addFilters callback that will be called to combine all following filter commands under a logical **NOT** group
     */
    public filterNot(addFilters: (query: ApiQuery) => void): this {
        const group = new ApiQueryFilterGroup('and');
        group.parenthesize = false;

        const filter = new ApiQueryNotFilter(group);
        this.internals.filterGroups.push(group);
        try {
            addFilters(this);
        }
        finally {
            this.internals.filterGroups.pop();
        }
        if(group.filters.length > 0) {
            this.getCurrentFilterGroup().filters.push(filter);
        }
        return this;
    }

    /**
     * Filters a detail collection of models
     *
     * @example
     * ```ts
     * query.filterExists('tasks', exists => exists
     *     .filterBoolean('isCustom', false)
     *     .filterBoolean('isCompleted', true)
     * );
     * ```
     * @param relationship name of the detail property to query
     * @param addFilters callback that will be called to combine all following filter commands under the **EXISTS** sub-query *(optional)*
     */
    public filterExists(relationship: string, addFilters?: (query: ApiQuery) => void, options?: Partial<IQueryExistsFilterOptions>): this {
        if(!relationship) { return this; }
        const appliedOptions = this.filterDefaults(options);

        if(addFilters) {
            const group = new ApiQueryFilterGroup('and');
            group.parenthesize = false;
            const filter = new ApiQueryExistsFilter(relationship, group);
            this.internals.filterGroups.push(group);
            try {
                addFilters(this);
            }
            finally {
                this.internals.filterGroups.pop();
            }
            if(group.filters.length > 0 || !appliedOptions.omitWhenEmpty) {
                this.getCurrentFilterGroup().filters.push(filter);
            }
        }
        else {
            const filter = new ApiQueryExistsFilter(relationship, null);
            this.getCurrentFilterGroup().filters.push(filter);
        }
        return this;
    }

    /**
     * Filter a model identifier (`id`) property value
     * @param field name of the model property
     * @param value value to compare against
     * @param operatorOrOptions `'eq'`, `'ne'`, or an `IQueryIdentifierFilterOptions` object *(optional)*
     */
    public filterIdentifier(field: string, value: string, operatorOrOptions?: QueryEqualityOperator | Partial<IQueryIdentifierFilterOptions>): this {
        const options = this.filterDefaults(operatorOrOptions);

        if(!field || (!value && options.ignoreNullValue)) {
            return this;
        }
        this.getCurrentFilterGroup().filters.push(new ApiQueryIdentifierFilter(field, value, options));
        return this;
    }

    /**
     * Filter a model identifier (`id`) property value against multiple options
     * Filter a model property specifying an array of identifier (id) values
     * @param field name of the model property
     * @param values values to compare against
     * @param operatorOrOptions `'eq'`, `'ne'`, or an `IQueryArrayFilterOptions` object *(optional)*
     */
    public filterIdentifierArray(field: string, values: string[], operatorOrOptions?: QueryEqualityOperator | Partial<IQueryArrayFilterOptions>): this {
        const options = this.filterDefaults(operatorOrOptions);

        if(!field || (values.length === 0 && options.ignoreNullValue)) {
            return this;
        }
        this.getCurrentFilterGroup().filters.push(new ApiQueryIdentifierArrayFilter(field, values, options));
        return this;
    }

    /**
     * Filter a model `string` property value
     * @param field name of the model property
     * @param value values to compare against
     * @param operatorOrOptions `'eq'`, `'ne'`, `'gt'`, `'lt'`, `'gte'`, `'lte'`, `'contains'`, `'notContains'`, `'startsWith'`, `'notStartsWith'`, `'endsWith'`, `'notEndsWith'`, or an `IQueryStringFilterOptions` object *(optional)*
     */
    public filterString(field: string, value: string | null, operatorOrOptions?: QueryStringComparisonOperator | Partial<IQueryStringFilterOptions>): this {
        const options = this.filterDefaults(operatorOrOptions);

        if(!field || (!value && options.ignoreNullValue)) {
            return this;
        }
        this.getCurrentFilterGroup().filters.push(new ApiQueryStringFilter(field, value, options));
        return this;
    }

    /**
     * Filter a model `string` property value against multiple options
     * Filter a model property specifying an array of `string` values
     * @param field name of the model property
     * @param values values to compare against
     * @param operatorOrOptions `'eq'`, `'ne'`, or an `IQueryArrayFilterOptions` object *(optional)*
     */
    public filterStringArray(field: string, values: string[], operatorOrOptions?: QueryEqualityOperator | Partial<IQueryArrayFilterOptions>): this {
        const options = this.filterDefaults(operatorOrOptions);

        if(!field || (values.length === 0 && options.ignoreNullValue)) {
            return this;
        }
        this.getCurrentFilterGroup().filters.push(new ApiQueryStringArrayFilter(field, values, options));
        return this;
    }

    /**
     * Filter a model `boolean` property value against `true`, `false`, or `null`
     * @param field name of the model property
     * @param value value to compare against
     * @param options an `IQueryBooleanFilterOptions` object *(optional)*
     */
    public filterBoolean(field: string, value: boolean | null, options?: Partial<IQueryBooleanFilterOptions>): this {
        const appliedOptions = this.filterDefaults(options);
        this.getCurrentFilterGroup().filters.push(new ApiQueryBooleanFilter(field, value, appliedOptions));
        return this;
    }

    // TODO: Document
    /**
     * ***To Be Provided***
     * @param field
     * @param value
     * @param modelCode
     */
    public filterSession(field: string, value: QuerySessionValue, modelCode?: string) {
        if(!field) { return this; }
        this.getCurrentFilterGroup().filters.push(new ApiQuerySessionFilter(field, value, modelCode));
        return this;
    }

    /**
     * Filter a model property (of any type) against `null`
     * @param field name of the model property
     * @param isNull `true` *(default)* to check if the value **IS** `null`, or `false` to check if the value **IS NOT** `null` *(optional)*
     */
    public filterIsNull(field: string, isNull: boolean = true): this {
        if(!field) { return this; }

        this.getCurrentFilterGroup().filters.push(new ApiQueryIsNullFilter(field, isNull));
        return this;
    }

    /**
     * Filter the model using a raw API filter query expression
     *
     * **Warning**: Only use in advanced scenarios where existing filter functions will not work
     * @param filter the API filter query expression *(see EssetteSuite API documentation)*
     */
    public filterDirect(filter: string | null): this {
        if(!filter) { return this; }

        this.getCurrentFilterGroup().filters.push(new ApiQueryDirectFilter(filter));
        return this;
    }

    /**
     * Filter a model `ApiDate` property
     *
     * @param field field name of the model property (`ApiDate`)
     * @param value the value to compare against (`ApiDate`)
     * @param operatorOrOptions `'eq'`, `'ne'`, `'gt'`, `'lt'`, `'gte'`, `'lte'`, or an `IQueryDateTimeFilterOptions` object *(optional)*
     */
    public filterDate(field: string, value: DateValue | string | null, operatorOrOptions?: QueryComparisonOperator | Partial<IQueryDateTimeFilterOptions>): this {
        const options = this.filterDefaults(operatorOrOptions);
        if(!field || (options.ignoreNullValue && !value)) { return this; }

        this.getCurrentFilterGroup().filters.push(new ApiQueryDateFilter(field, value, options));
        return this;
    }

    /**
     * Filter a model `ApiDate` property against a special date, such as `'today'`
     *
     * @param field field name of the model property (`ApiDate`)
     * @param value the value to compare against (`QuerySpecialDate`)
     * @param operatorOrOptions `'eq'`, `'ne'`, `'gt'`, `'lt'`, `'gte'`, `'lte'`, or an `IQueryDateTimeFilterOptions` object *(optional)*
     */
    public filterDateSpecial(field: string, value: QuerySpecialDate | null, operatorOrOptions?: QueryComparisonOperator | Partial<IQueryDateTimeFilterOptions>): this {
        const options = this.filterDefaults(operatorOrOptions);
        if(!field || (options.ignoreNullValue && !value)) { return this; }

        this.getCurrentFilterGroup().filters.push(new ApiQuerySpecialDateFilter(field, value, options));
        return this;
    }

    /**
     * Filter a model `ApiDate` property to values in the specified `ApiDate` range
     * * `field`: **ApiDate** `BETWEEN` `lowerBound`: **ApiDate** `AND` `upperBound`: **ApiDate**
     * * `field`: **ApiDate** `>=` `lowerBound`: **ApiDate**
     *
     * @param field field name of the model property (`ApiDate`)
     * @param lowerBound lower bound (`ApiDate`)
     * @param upperBound upper bound (`ApiDate`, *optional*)
     * @param options an `IQueryDateTimeBetweenFilterOptions` object *(optional)*
     */
    public filterDateBetween(field: string, lowerBound: DateValue | string, upperBound: DateValue | string | null = null, options?: Partial<IQueryDateTimeBetweenFilterOptions>): this {
        const appliedOptions = this.filterDefaults(options);
        if(!field) { return this; }

        this.getCurrentFilterGroup().filters.push(new ApiQueryDateBetweenFilter(field, lowerBound, upperBound, appliedOptions));
        return this;
    }

    /**
     * Filter a model `ApiDateTime` property
     *
     * @param field field name of the model property (`ApiDateTime`)
     * @param value the value to compare against (`ApiDateTime`)
     * @param options an `IQueryDateTimeFilterOptions` object *(optional)*
     * @param operatorOrOptions `'eq'`, `'ne'`, `'gt'`, `'lt'`, `'gte'`, `'lte'`, or an `IQueryDateTimeFilterOptions` object *(optional)*
     */
    public filterDateTime(field: string, value: DateTimeValue | string | null, operatorOrOptions?: QueryComparisonOperator | Partial<IQueryDateTimeFilterOptions>): this {
        const options = this.filterDefaults(operatorOrOptions);
        if(!field || (options.ignoreNullValue && !value)) { return this; }

        this.getCurrentFilterGroup().filters.push(new ApiQueryDateTimeFilter(field, value, options));
        return this;
    }

    /**
     * Filter a model `ApiDateTime` property against a special date, such as `'now'`
     *
     * @param field field name of the model property (`ApiDateTime`)
     * @param value the value to compare against (`QuerySpecialDateTime`)
     * @param operatorOrOptions `'eq'`, `'ne'`, `'gt'`, `'lt'`, `'gte'`, `'lte'`, or an `IQueryDateTimeFilterOptions` object *(optional)*
     */
    public filterDateTimeSpecial(field: string, value: QuerySpecialDateTime | null, operatorOrOptions?: QueryComparisonOperator | Partial<IQueryDateTimeFilterOptions>): this {
        const options = this.filterDefaults(operatorOrOptions);
        if(!field || (options.ignoreNullValue && !value)) { return this; }

        this.getCurrentFilterGroup().filters.push(new ApiQuerySpecialDateTimeFilter(field, value, options));
        return this;
    }

    /**
     * Filter a model `ApiDateTime` property
     * * `field`: **ApiDateTime** `BETWEEN` `value`: **ApiDate** `AND` `value + 1 DAY`: **ApiDate**
     *
     * @param field field name of the model property (`ApiDateTime`)
     * @param value the value to compare against (`ApiDate`)
     * @param options an `IQueryDateTimeBetweenFilterOptions` object *(optional)*
     * @param operatorOrOptions `'eq'`, `'ne'`, `'gt'`, `'lt'`, `'gte'`, `'lte'`, or an `IQueryDateTimeBetweenFilterOptions` object *(optional)*
     */
    public filterDateTimeOnDate(field: string, value: DateValue | string | null, options?: Partial<IQueryDateTimeOnFilterOptions>): this {
        const appliedOptions = this.filterDefaults(options);
        if(!field || (appliedOptions.ignoreNullValue && !value)) { return this; }

        this.getCurrentFilterGroup().filters.push(new ApiQueryDateTimeOnDateFilter(field, value, appliedOptions));
        return this;
    }

    /**
     * Filter a model `ApiDateTime` property to values in the specified `ApiDate` range
     * * `field`: **ApiDateTime** `BETWEEN` `lowerBound`: **ApiDate** `AND` `upperBound`: **ApiDate**
     * * `field`: **ApiDateTime** `>=` `lowerBound`: **ApiDate**
     *
     * @param field field name of the model property (`ApiDateTime`)
     * @param lowerBound lower bound (`ApiDate`)
     * @param upperBound upper bound (`ApiDate`, *optional*)
     * @param options an `IQueryDateTimeBetweenFilterOptions` object *(optional)*
     */
    public filterDateTimeBetweenDates(field: string, lowerBound: DateValue | string, upperBound: DateValue | string | null = null, options?: Partial<IQueryDateTimeBetweenFilterOptions>): this {
        const appliedOptions = this.filterDefaults(options);
        if(!field) { return this; }

        this.getCurrentFilterGroup().filters.push(new ApiQueryDateTimeBetweenDatesFilter(field, lowerBound, upperBound, appliedOptions));
        return this;
    }

    /**
     * Filter a model `ApiDateTime` property to values in the specified `ApiDateTime` range
     * * `field`: **ApiDateTime** `BETWEEN` `lowerBound`: **ApiDateTime** `AND` `upperBound`: **ApiDateTime**
     * * `field`: **ApiDateTime** `>=` `lowerBound`: **ApiDateTime**
     *
     * @param field field name of the model property (`ApiDateTime`)
     * @param lowerBound lower bound (`ApiDateTime`)
     * @param upperBound upper bound (`ApiDateTime`, *optional*)
     * @param options an `IQueryDateTimeBetweenFilterOptions` object *(optional)*
     */
    public filterDateTimeBetween(field: string, lowerBound: DateTimeValue | string, upperBound: DateTimeValue | string | null = null, options?: Partial<IQueryDateTimeBetweenFilterOptions>): this {
        const appliedOptions = this.filterDefaults(options);
        if(!field) { return this; }

        this.getCurrentFilterGroup().filters.push(new ApiQueryDateTimeBetweenFilter(field, lowerBound, upperBound, appliedOptions));
        return this;
    }

    /**
     * Specify the sort group of the query
     * @param references array of model property names as a `string` or `IQuerySortGroup` objects
     */
    public group(...references: (IQuerySortGroup)[]): this {
        for(let reference of references) {
            let sortEntry: IQuerySortGroup;
            sortEntry = reference;

            const name = sortEntry.reference;
            if(!sortEntry.reference || !sortEntry.matchValue) {
                continue;
            }
            this.internals.groups.set(name, sortEntry);
        }
        return this;
    }

    /**
     * Specify the sort order of the query
     * @param references array of model property names as a `string` or `IQuerySort` objects
     */
    public order(...references: (IQuerySort | string)[]): this {
        for(let reference of references) {
            let sortEntry: IQuerySort;
            if(typeof reference === 'string') {
                sortEntry = { reference: reference as string };
            }
            else {
                sortEntry = reference;
            }
            const name = sortEntry.reference;
            if(!name) {
                continue;
            }
            this.internals.orders.set(name, sortEntry);
        }
        return this;
    }

    /**
     * Override the sorting (useful when default sorting is configured, but the user has overriden the order by clicking on table headers)
     * @param sorting an `IQuerySortingOptions` object that overrides the orders already specified by this query
     */
    public replaceSorting(sorting: Partial<IQuerySortingOptions> | null) {
        if(!sorting || !sorting.entries || !sorting.entries.length) {
            return this;
        }
        this.internals.orders.clear();
        this.order(...sorting.entries);
        return this;
    }

    /**
     * Apply the specified sorting for group matching
     * @param sorting an `IQuerySortingOptions` object that is combined with the groups already specified by this query
     */
    public applySortGroups(sorting: Partial<IQuerySortingOptions> | null) {
        if(!sorting || !sorting.groupEntries || !sorting.groupEntries.length) {
            return this;
        }
        this.group(...sorting.groupEntries);
        return this;
    }

    /**
     * Apply the specified sorting
     * @param sorting an `IQuerySortingOptions` object that is combined with the orders already specified by this query
     */
    public applySorting(sorting: Partial<IQuerySortingOptions> | null) {
        if(!sorting || !sorting.entries || !sorting.entries.length) {
            return this;
        }
        this.order(...sorting.entries);
        return this;
    }

    /**
     * Return only the number of records in the query results, not the query results themselves
     */
    public countOnly(): this {
        this.internals.page.count = 'only';
        return this;
    }

    /**
     * Do not return the total records count (for cheaper API querying)
     */
    public excludeTotalRecordsCount(): this {
        this.internals.page.count = 'none';
        return this;
    }

    /**
     * Skip the specified number of records when returning the query results
     * @param value the number of records to skip
     */
    public skip(value: number): this {
        if(value >= 0) {
            this.internals.page.skip = value;
        }
        return this;
    }

    /**
     * Limit the query results to the specified number of records
     * @param value the maximum number of records to return
     */
    public top(value: number): this {
        if(value > 0) {
            this.internals.page.top = value;
        }
        return this;
    }

    /**
     * Apply pagination options to the query
     * @param pagination an `IQueryPaginationOptions` object
     */
    public applyPagination(pagination: IQueryPaginationOptions | null): this {
        if(!pagination) {
            return this;
        }
        if(pagination.size !== undefined && pagination.size !== null && pagination.size > 0) {
            this.internals.page.top = pagination.size;
        }

        const top = this.internals.page.top;
        if(top && pagination.index !== undefined && pagination.index !== null && pagination.index >= 0) {
            this.internals.page.skip = (pagination.index * top);
        }

        return this;
    }

    // TODO: Document
    /**
     * ***To Be Provided***
     * @param value
     */
    public detailSkip(value: number): this {
        if(value >= 0) {
            this.internals.detailPage.skip = value;
        }
        return this;
    }

    // TODO: Document
    /**
     * ***To Be Provided***
     * @param value
     */
    public detailTop(value: number): this {
        if(value > 0) {
            this.internals.detailPage.top = value;
        }
        return this;
    }

    // TODO: Document
    /**
     * ***To Be Provided***
     * @param pagination
     */
    public detailPage(pagination: IQueryPaginationOptions): this {
        if(pagination.size !== undefined && pagination.size !== null && pagination.size > 0) {
            this.internals.detailPage.top = pagination.size;
        }
        const detailTop = this.internals.detailPage.top;
        if(detailTop && pagination.index !== undefined && pagination.index !== null && pagination.size >= 0) {
            this.internals.detailPage.skip = (pagination.index * detailTop);
        }
        return this;
    }

    // TODO: Document
    /**
     * ***To Be Provided***
     * @param name
     * @param value
     */
    public setParameter(name: string, value: string): this {
        this.internals.parameters.set(name, value);
        return this;
    }

    // TODO: Document
    /**
     * ***To Be Provided***
     * @param parameters
     */
    public setParameters(parameters?: ReadonlyIndexed<string>): this {
        if(!parameters) { return this; }

        for(const key in parameters) {
            const value = parameters[key];
            if(value) {
                this.internals.parameters.set(key, value);
            }
            else {
                this.internals.parameters.delete(key);
            }
        }
        return this;
    }

    // TODO: Document
    /**
     * ***To Be Provided***
     * @param loadOptions
     * @param applyOptions
     */
    public applyStandardLoadOptions(
        loadOptions: Partial<IQueryLoadOptions> | null = null,
        applyOptions: Partial<IApiStandardLoadOptionsApplyOptions> | null = null): this {
        if(!loadOptions) {
            return this;
        }

        this.applySorting(loadOptions.sorting || null)
            .applySortGroups(loadOptions.sorting || null)
            .applyPagination(loadOptions.pagination || null)
            .includeInactive(loadOptions.includeInactive || false);

        // apply all/top
        if(loadOptions.top !== undefined && loadOptions.top !== null) {
            this.top(loadOptions.top);
        }
        else {
            // for now, default to limit=all when "all" is not specified here
            // later, with more testing, we can assume assume a smaller limit if "all" is not specified
            if(loadOptions.all !== false) {
                if(loadOptions.all !== true) {
                    // TODO: change this to a warning and start looking for it
                    console.debug('API: query loadOptions without "top" or "all" option');
                }
                this.limit('all');
                this.top(ApiQuery.allRecordLimit);

                // TODO: test and support detailLimit=all
                this.detailTop(ApiQuery.allDetailLimit);
            }
            else {
                this.top(ApiQuery.allRecordLimit);
                this.detailTop(ApiQuery.allDetailLimit);
            }
        }

        // apply page/skip
        if(loadOptions.page !== undefined && loadOptions.page !== null) {
            if(this.internals.page.top !== null) {
                this.skip(this.internals.page.top * loadOptions.page);
            }
        }
        else if(loadOptions.skip !== undefined && loadOptions.skip !== null) {
            this.skip(loadOptions.skip);
        }

        if(applyOptions &&
            applyOptions.applyPaginationSizeToDetails &&
            loadOptions.pagination &&
            loadOptions.pagination.size) {
            this.detailTop(loadOptions.pagination.size);
        }
        return this;
    }

    private filterDefaults<T extends {}>(operatorOrOptions?: Partial<T> | string): T {
        // breaking lots of type safety here for convenience and compactness
        if(typeof operatorOrOptions === 'string') {
            return { ...ApiQuery.defaults.filterOptions, ...{ operator: operatorOrOptions } } as any as T;
        }
        if(operatorOrOptions) {
            return { ...ApiQuery.defaults.filterOptions, ...(operatorOrOptions as any) } as any as T;
        }
        return { ...ApiQuery.defaults.filterOptions } as any as T;
    }

    /**
     * Convert the current query to an API filter expression
     */
    public toString(): string {
        if(!this.internals.url) {
            return '';
        }

        const slash = this.internals.url.endsWith('/') ? '' : '/';
        const url = this.internals.tenant ? `${encodeURI(this.internals.tenant)}/${this.internals.url}${slash}` : this.internals.url;
        const query: string[] = [];

        if(this.internals.includes.size > 0) {
            const includes: string[] = [];
            for(let includePair of this.internals.includes) {
                const queryInclude = includePair[1];
                let includeParts: string[] = [encodeQueryString(queryInclude.reference)];
                if(queryInclude.state === 'active') {
                    includeParts.push(':active');
                }
                else if(queryInclude.state === 'inactive') {
                    includeParts.push(':inactive');
                }
                else if(queryInclude.state === 'inactiveOnly') {
                    includeParts.push(':inactiveOnly');
                }
                includes.push(includeParts.join(''));
            }
            query.push(`include=${includes.join('+')}`);
        }

        if(this.internals.fields.length > 0) {
            query.push(`fields=${this.internals.fields.join('+')}`);
        }

        if(this.internals.feature) {
            query.push(`feature=${this.internals.feature}`);
        }

        //TODO: Figure out safest way to convet enum to string
        if(this.internals.state) {
            query.push(`state=${this.internals.state}`);
        }

        if(this.internals.strategy) {
            query.push(`strategy=${this.internals.strategy}`);
        }

        const attachedToCode = this.internals.attachedTo.code;
        const attachedToId = this.internals.attachedTo.id;
        if(attachedToCode && attachedToId) {
            query.push(
                `attachedTo=${encodeQueryString(attachedToCode)}:${encodeQueryString(attachedToId)}`);
        }

        const search = this.internals.search;
        if(search) {
            query.push(`search=${encodeQueryString(search)}`);
        }

        const filter = this.internals.filterGroups[0].toString();
        if(filter) {
            query.push(`filter=${filter}`);
        }

        if(this.internals.groups.size > 0) {
            for(const group of this.internals.groups) {
                const querySortGroup = group[1];
                let groupParts: string[] = [];

                groupParts.push(encodeQueryString(querySortGroup.reference))
                groupParts.push(' eq \'' + encodeQueryString(querySortGroup.matchValue) + '\'');

                query.push(`group=${groupParts.join('')}`);
            }
        }

        if(this.internals.orders.size > 0) {
            const orders: string[] = [];
            for(const orderPair of this.internals.orders) {
                const querySort = orderPair[1];

                // This supports multiple references in a single string delimited by space.
                // Each entry gets the same ascending/descending setting.
                // This makes it easy to sort by multiple fields easily in a table column. -mwj
                const matchSpaces = /\s+/g;
                for(const ref of querySort.reference.split(matchSpaces)) {
                    let orderParts: string[] = [encodeQueryString(ref)];
                    if(querySort.direction === 'descending') {
                        orderParts.push(':desc');
                    }
                    if(querySort.nulls === 'first') {
                        orderParts.push(':nullsFirst');
                    }
                    else if(querySort.nulls === 'last') {
                        orderParts.push(':nullsLast');
                    }
                    orders.push(orderParts.join(''));
                }
            }
            query.push(`order=${orders.join('+')}`);
        }

        const limit = this.internals.page.limit;
        if(limit) {
            query.push(`limit=${limit}`);
        }

        const skip = this.internals.page.skip;
        if(skip) {
            query.push(`skip=${Math.floor(skip)}`);
        }

        const top = this.internals.page.top;
        if(top !== null) {
            query.push(`top=${Math.floor(top)}`);
        }

        const count = this.internals.page.count;
        if(count) {
            query.push(`count=${count}`);
        }

        const detailLimit = this.internals.detailPage.limit;
        if(detailLimit) {
            query.push(`detailLimit=${detailLimit}`);
        }

        const detailSkip = this.internals.detailPage.skip;
        if(detailSkip) {
            query.push(`detailSkip=${Math.floor(detailSkip)}`);
        }

        const detailTop = this.internals.detailPage.top;
        if(detailTop !== null) {
            query.push(`detailTop=${Math.floor(detailTop)}`);
        }

        for(let parameterPair of this.internals.parameters) {
            query.push(`${encodeQueryString(parameterPair[0])}=${encodeQueryString(parameterPair[1])}`);
        }

        return (query.length > 0 ? (`${url}?${query.join('&')}`) : url);
    }

}
